import { z } from "zod";
import { ROLES } from "./roles";

/** Milliseconds from the recording origin. Always a non-negative integer. */
const ms = z.number().int().nonnegative();

/**
 * `upload` and `studio` are not conferencing platforms — the first means the
 * file was uploaded, the second that it was published from a Studio edit.
 *
 * `youtube` and `linkedin` are where an imported video came from. They are the
 * ids in `IMPORT_SOURCES`, and `imports.test.ts` fails if the two lists drift —
 * a platform missing here would make every recording imported from it fail
 * response validation in the client.
 */
export const platformSchema = z.enum([
  "zoom",
  "google_meet",
  "teams",
  "upload",
  "studio",
  "youtube",
  "linkedin",
]);
export type Platform = z.infer<typeof platformSchema>;

export const recordingStatusSchema = z.enum([
  "scheduled", // bot dispatched, meeting not started
  "recording", // bot is in the meeting
  "processing", // media and transcript being ingested
  "ready",
  "failed",
]);
export type RecordingStatus = z.infer<typeof recordingStatusSchema>;

export const roleSchema = z.enum(ROLES);

// ─── Reads ───────────────────────────────────────────────────────────────────

export const participantSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email().nullable(),
  isHost: z.boolean(),
});
export type Participant = z.infer<typeof participantSchema>;

export const wordSchema = z.object({
  text: z.string(),
  startMs: ms,
  endMs: ms,
});
export type Word = z.infer<typeof wordSchema>;

export const utteranceSchema = z.object({
  id: z.string().uuid(),
  participantId: z.string().uuid().nullable(),
  /**
   * Which speaker this line is attributed to. Nullable because an utterance
   * whose speaker the provider could not identify has no cluster at all.
   *
   * The client needs this to offer re-assignment: `speakerLabel` is a display
   * string and several speakers can legitimately share one.
   */
  clusterId: z.string().uuid().nullable(),
  speakerLabel: z.string(), // resolved display name, or "Speaker 2" when unknown
  startMs: ms,
  endMs: ms,
  text: z.string(),
  words: z.array(wordSchema),
});
export type Utterance = z.infer<typeof utteranceSchema>;

/**
 * A distinct voice in one recording.
 *
 * Named by the platform for bot recordings (Recall knows who joined) and
 * inferred for uploads, where diarization can only say "these lines share a
 * voice" and somebody has to say whose.
 */
export const speakerSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  participantId: z.string().uuid().nullable(),
  /** platform | inferred | manual — manual means a person corrected it. */
  resolvedBy: z.string(),
  /** Lines currently attributed here. Zero after everything was re-assigned away. */
  utteranceCount: z.number().int().nonnegative(),
});
export type Speaker = z.infer<typeof speakerSchema>;

/**
 * Bounded so a label stays a name rather than becoming a note. Trimmed before
 * the length check, so trailing spaces cannot smuggle in an empty one.
 */
export const speakerLabelSchema = z.string().trim().min(1).max(80);

export const chapterSchema = z.object({
  id: z.string().uuid(),
  startMs: ms,
  endMs: ms,
  title: z.string(),
  summary: z.string(),
});
export type Chapter = z.infer<typeof chapterSchema>;

export const recordingSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  platform: platformSchema,
  status: recordingStatusSchema,
  /**
   * Why the recording is in this state — shown next to the status.
   *
   * Present on `failed`, and also on `ready` when something partial happened
   * (an upload stored without a transcript, say). A badge the user cannot
   * interpret is worse than no badge.
   */
  failureReason: z.string().nullable(),
  /**
   * What is happening right now, in a sentence.
   *
   * Derived on the server from the raw provider status, so the wording lives in
   * one place instead of being reconstructed by every client that renders a
   * badge. Null when the status is self-explanatory (`ready`).
   */
  statusMessage: z.string().nullable(),
  /**
   * Two or three words for the badge, when the provider told us something more
   * specific than our five states can express.
   *
   * "Not let in" and "Password needed" are both `failed`, and a badge reading
   * "Failed" for either is true and useless — the point of the badge is that
   * someone can tell what happened without opening anything.
   */
  statusLabel: z.string().nullable(),
  /**
   * True when the recording will not progress on its own.
   *
   * The distinction the badge alone cannot make: "waiting to be admitted" and
   * "joining the call" are both pre-recording states, but only one of them
   * needs a human to go and do something.
   */
  needsAttention: z.boolean().default(false),
  /**
   * Where an import from a link has got to; null once its media has landed,
   * and for anything not imported. The import dialog waits on `checking` so
   * a link with no video is reported while the person is still there.
   */
  importStage: z.enum(["checking", "downloading", "storing"]).nullable().default(null),
  startedAt: z.string().datetime(),
  durationMs: ms.nullable(),
  projectId: z.string().uuid().nullable(),
  /** Poster frame URL, when the worker has captured one. Cards fall back. */
  thumbnailUrl: z.string().url().nullable().default(null),
  /** Stored media size. Null before the media lands. */
  sizeBytes: z.number().int().nullable().default(null),
  participants: z.array(participantSchema),
});
export type RecordingSummary = z.infer<typeof recordingSummarySchema>;

export const recordingDetailSchema = recordingSummarySchema.extend({
  /** Short-lived signed URL. Never cache this in client storage. */
  mediaUrl: z.string().url().nullable(),
  /**
   * `hls` once transcoding has produced a ladder, `mp4` before that.
   *
   * Both are playable. HLS starts after one segment and adapts to bandwidth;
   * the MP4 is the original and only plays as fast as it downloads.
   */
  mediaType: z.enum(["hls", "mp4"]).default("mp4"),
  chapters: z.array(chapterSchema),
  /** null when nobody has asked yet. */
  chaptersStatus: z.enum(["generating", "ready", "failed"]).nullable(),
  /** Why there are no chapters. Present only when `chaptersStatus` is failed. */
  chaptersError: z.string().nullable(),
  /**
   * `running` while the streaming encode is in progress, `failed` if it gave
   * up, null once `mediaType` answers the question.
   *
   * Needed because nothing reaches storage until the encode finishes, so
   * `mediaType: "mp4"` on its own cannot distinguish "still being prepared"
   * from "nobody ever prepared it".
   */
  transcodeStatus: z.enum(["running", "failed"]).nullable().default(null),
  transcodeError: z.string().nullable().default(null),
  /** Set when this recording was published from Studio. */
  /**
   * The page an imported video was fetched from, so the original is one click
   * away. Null for everything that was not imported.
   */
  sourceUrl: z.string().url().nullable().default(null),
  derivedFrom: z
    .object({ id: z.string().uuid(), title: z.string() })
    .nullable()
    .default(null),
  /** Recordings that were published from edits of this one. */
  edits: z
    .array(z.object({ id: z.string().uuid(), title: z.string() }))
    .default([]),
});
export type RecordingDetail = z.infer<typeof recordingDetailSchema>;

export const transcriptSchema = z.object({
  recordingId: z.string().uuid(),
  utterances: z.array(utteranceSchema),
  /**
   * Every speaker in the recording, including any left with no lines after a
   * re-assignment — dropping those would make an empty speaker disappear from
   * the menu the moment somebody moved its last line, which is exactly when
   * they might want to move one back.
   */
  speakers: z.array(speakerSchema),
});
export type Transcript = z.infer<typeof transcriptSchema>;

// ─── Upload limits ───────────────────────────────────────────────────────────

/**
 * What may be uploaded, in one place because the browser and the API must
 * agree. They previously did not agree with reality at all: the cap was 5 GB —
 * the ceiling of a single presigned PUT — while transcription refuses anything
 * over 3 GB or 10 hours. Files between 3 and 5 GB therefore uploaded happily,
 * played happily, and could never be transcribed, and nobody was told either
 * before or after.
 *
 * The transcription limits are the real ones, so they are the ones enforced. A
 * meeting recording with no transcript is missing most of what this product
 * does, which is why these refuse rather than warn.
 */
export const UPLOAD_LIMITS = {
  maxBytes: 3 * 1024 * 1024 * 1024,
  maxDurationMs: 10 * 60 * 60 * 1000,
  /**
   * Size of each multipart chunk.
   *
   * Above S3's 5 MB minimum by a wide margin and well under the 10,000-part
   * ceiling: at 64 MB, the 3 GB maximum is 48 parts. Larger parts mean fewer
   * signatures and fewer requests; smaller parts mean less to redo when one
   * fails. This is the middle.
   */
  partBytes: 64 * 1024 * 1024,
  /** Extensions are not checked — the browser's MIME type is. */
  acceptedTypes: [
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "audio/mpeg",
    "audio/mp4",
    "audio/wav",
    "audio/x-m4a",
  ],
} as const;

/**
 * Upload progress, as a whole percent.
 *
 * `settled` is bytes S3 has confirmed; `inFlight` is bytes currently being sent
 * for parts that have not finished. Both are needed, and the bug this replaced
 * was counting a part only while it was in flight — so completing a part
 * removed its bytes from the total and the bar ran backwards before climbing
 * again, over and over, for the whole upload.
 *
 * Caps at 99: the last byte leaving the browser is not the same as the object
 * existing, and the difference is the completion call.
 */
export function uploadPercent(
  settled: number,
  inFlight: number,
  totalBytes: number,
): number {
  if (totalBytes <= 0) return 0;
  const sent = Math.min(settled + inFlight, totalBytes);
  return Math.max(0, Math.min(99, Math.round((sent / totalBytes) * 100)));
}

/** "3 GB", for telling somebody the limit before they hit it. */
export function formatBytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${Number(gb.toFixed(gb < 10 ? 1 : 0))} GB`;
  const mb = bytes / 1024 ** 2;
  if (mb >= 1) return `${Math.round(mb)} MB`;
  // The KB rung matters now that size is a first-class sort: a 55 KB clip
  // reading "0 MB" is an unreadable label, not a small file.
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// ─── Studio & versions ───────────────────────────────────────────────────────

/** One stored chapter set, as the versions picker lists it. */
export const chapterSetInfoSchema = z.object({
  id: z.string().uuid(),
  source: z.enum(["ai", "manual"]),
  label: z.string(),
  model: z.string().nullable(),
  chapterCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  /** True when this snapshot is what the recording currently shows. */
  isLive: z.boolean(),
});
export type ChapterSetInfo = z.infer<typeof chapterSetInfoSchema>;

export const summaryVersionInfoSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  model: z.string().nullable(),
  createdAt: z.string(),
  isLive: z.boolean(),
});
export type SummaryVersionInfo = z.infer<typeof summaryVersionInfoSchema>;

export const studioProjectInfoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  sourceRecordingId: z.string().uuid().nullable(),
  publishedRecordingId: z.string().uuid().nullable(),
  clipCount: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  updatedAt: z.string(),
});
export type StudioProjectInfo = z.infer<typeof studioProjectInfoSchema>;

export const publishProjectSchema = z.object({
  title: z.string().trim().min(1).max(200),
  /** map = carry transcripts through the cuts; asr = re-transcribe; none. */
  transcriptMode: z.enum(["map", "asr", "none"]),
});
export type PublishProjectInput = z.infer<typeof publishProjectSchema>;

/** One translation of a recording's transcript, as the language menu lists it. */
export const transcriptTranslationInfoSchema = z.object({
  id: z.string().uuid(),
  language: z.string(),
  /** generating | ready | failed */
  status: z.string(),
  error: z.string().nullable(),
  model: z.string().nullable(),
  createdAt: z.string(),
});
export type TranscriptTranslationInfo = z.infer<typeof transcriptTranslationInfoSchema>;

/** A requested subtitle-burned export of a recording. */
export const recordingExportInfoSchema = z.object({
  id: z.string().uuid(),
  /** `original`, or a translation language code. */
  subtitleLanguage: z.string(),
  /** generating | ready | failed */
  status: z.string(),
  error: z.string().nullable(),
  createdAt: z.string(),
});
export type RecordingExportInfo = z.infer<typeof recordingExportInfoSchema>;

// ─── Writes ──────────────────────────────────────────────────────────────────

/**
 * Rename a recording. Bounded like the title given at creation, since it is the
 * same column — a rename must not be able to store what a create could not.
 */
export const renameRecordingSchema = z.object({
  title: z.string().trim().min(1).max(200),
});
export type RenameRecordingInput = z.infer<typeof renameRecordingSchema>;

/** Rename a speaker. One row changes and every line they said follows. */
export const renameSpeakerSchema = z.object({ label: speakerLabelSchema });
export type RenameSpeakerInput = z.infer<typeof renameSpeakerSchema>;

/**
 * Move one line to a different speaker.
 *
 * Two shapes because diarization fails in two directions. When it splits one
 * person across several voices, the fix is an existing `clusterId`. When it
 * merges two people into one, there is no correct speaker to point at yet, so
 * `newSpeakerLabel` creates one — without it the merged case would be
 * unfixable, and it is the more common of the two.
 */
export const reassignUtteranceSchema = z
  .object({
    clusterId: z.string().uuid().optional(),
    newSpeakerLabel: speakerLabelSchema.optional(),
  })
  .refine(
    (input) => (input.clusterId ? 1 : 0) + (input.newSpeakerLabel ? 1 : 0) === 1,
    { message: "Provide exactly one of clusterId or newSpeakerLabel." },
  );
export type ReassignUtteranceInput = z.infer<typeof reassignUtteranceSchema>;

export const createMeetingSchema = z.object({
  meetingUrl: z.string().url(),
  title: z.string().min(1).max(200).optional(),
  projectId: z.string().uuid().nullable().optional(),
  /** Overrides the tenant default. Participants see this in the roster. */
  botName: z.string().min(1).max(60).optional(),
  /**
   * When the bot should join, as ISO 8601. Omit to join immediately.
   *
   * Recall asks for at least ten minutes of lead time on scheduled joins —
   * they provision capacity ahead of the call, and a bot booked for two minutes
   * from now may simply miss the start.
   */
  joinAt: z.string().datetime().optional(),
});
export type CreateMeetingInput = z.infer<typeof createMeetingSchema>;

/**
 * Import a video from a link.
 *
 * The URL is only checked for shape here. Whether it is a platform we import
 * from is `recogniseImportUrl`'s call, made on the server, because the answer
 * has to come with the canonical form that is actually stored.
 */
export const createImportSchema = z.object({
  url: z.string().trim().min(1).max(2048),
  /** Omit to use the video's own title once it has been fetched. */
  title: z.string().trim().min(1).max(200).optional(),
  projectId: z.string().uuid().nullable().optional(),
});
export type CreateImportInput = z.infer<typeof createImportSchema>;

export const createProjectSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).optional(),
});

export const inviteMemberSchema = z.object({
  email: z.string().email(),
  role: roleSchema,
});

/**
 * Search is read from the query string, where every value arrives as text.
 * `coerce` is what makes `?limit=20` valid rather than a 400 — a plain
 * `z.number()` here rejects every real request from a browser.
 */
export const searchSchema = z.object({
  q: z.string().min(2).max(200),
  projectId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const searchHitSchema = z.object({
  recordingId: z.string().uuid(),
  recordingTitle: z.string(),
  utteranceId: z.string().uuid(),
  startMs: ms,
  speakerLabel: z.string(),
  snippet: z.string(),
});
export type SearchHit = z.infer<typeof searchHitSchema>;

// ─── Errors ──────────────────────────────────────────────────────────────────

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

// ─── Meeting summary ─────────────────────────────────────────────────────────

/**
 * One line of a generated summary, anchored to the moment it was said.
 *
 * `startMs` is resolved on the server from an utterance id the model returned —
 * the model never sees or emits a time. That is what makes every one of these
 * clickable and exact rather than approximately right. See CLAUDE.md
 * invariant 3.
 *
 * Null when the model cited an utterance that does not exist. The line is still
 * shown, because a true statement about the meeting is worth reading even if we
 * cannot jump to it; it simply is not a link.
 */
export const summaryPointSchema = z.object({
  text: z.string(),
  startMs: ms.nullable(),
  speaker: z.string().nullable(),
});
export type SummaryPoint = z.infer<typeof summaryPointSchema>;

export const summaryActionSchema = summaryPointSchema.extend({
  /** Who took it on, when the transcript says. Never guessed. */
  owner: z.string().nullable(),
  /** Whatever deadline was spoken — "by Friday", "end of Q3". Free text on purpose. */
  due: z.string().nullable(),
});
export type SummaryAction = z.infer<typeof summaryActionSchema>;

export const summaryQuestionSchema = summaryPointSchema.extend({
  askedBy: z.string().nullable(),
});
export type SummaryQuestion = z.infer<typeof summaryQuestionSchema>;

export const summaryStatusSchema = z.enum(["generating", "ready", "failed"]);

/**
 * The AI summary of one meeting.
 *
 * Generated on demand and shared by the whole workspace: one person clicks
 * generate, everybody sees the result. Regenerating replaces it for everyone,
 * which is the intended behaviour — a summary is a property of the meeting,
 * not of the reader.
 */
export const meetingSummarySchema = z.object({
  status: summaryStatusSchema,
  /** Why generation failed. Null unless `status` is `failed`. */
  failureReason: z.string().nullable(),
  /** A few paragraphs on what the meeting was about and where it landed. */
  overview: z.string(),
  keyPoints: z.array(summaryPointSchema),
  decisions: z.array(summaryPointSchema),
  actions: z.array(summaryActionSchema),
  openQuestions: z.array(summaryQuestionSchema),
  generatedByModel: z.string().nullable(),
  generatedAt: z.string().datetime().nullable(),
  /** Display name of whoever asked for it, so a regenerate is attributable. */
  generatedBy: z.string().nullable(),
});
export type MeetingSummary = z.infer<typeof meetingSummarySchema>;


/**
 * What each status is called, for a reader.
 *
 * Lives here because two screens rendered the same record differently — the
 * library said "Scheduled" and the detail page said "Processing" — by each
 * inventing its own fallback. `statusLabel` from the API is more specific when
 * it exists; this is what to say when it does not.
 */
export const STATUS_LABEL: Record<RecordingStatus, string> = {
  scheduled: "Scheduled",
  recording: "Recording",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
};
